import React, { useState, useEffect } from 'react';
import { XIcon } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import { useClients, type ClientRecord } from '../hooks/useClients';
import { Payment } from '../hooks/useAllPayments';

interface PaymentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingPayment: Payment | null;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  clientId?: string;
}

type PaymentFormState = {
  clientId: string;
  amount: string;
  paymentMethod: string;
  status: 'pending' | 'paid' | 'expired';
  dueDate: string;
  paidDate: string;
  notes: string;
};

function getInitialFormState(): PaymentFormState {
  const currentDate = getCurrentLocalDate();

  return {
    clientId: '',
    amount: '',
    paymentMethod: '',
    status: 'pending',
    dueDate: currentDate,
    paidDate: currentDate,
    notes: '',
  };
}

const paymentMethods = [
  'UPI',
  'Cash',
  'Debit Card',
  'Credit Card',
  'Bank Transfer',
];

function toDateInputValue(value?: string | null) {
  if (!value) return '';
  return value.split('T')[0] ?? '';
}

function combineDateTime(date: string, fallback?: string) {
  if (date) {
    // Use start of day (midnight) in local timezone
    const iso = new Date(`${date}T00:00:00`);
    if (!Number.isNaN(iso.getTime())) {
      return iso.toISOString();
    }
  }
  if (fallback) {
    const fallbackDate = new Date(fallback);
    if (!Number.isNaN(fallbackDate.getTime())) {
      return fallbackDate.toISOString();
    }
  }
  return null;
}

function getCurrentLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function PaymentFormModal({
  isOpen,
  onClose,
  editingPayment,
  onSuccess,
  onError,
  clientId,
}: PaymentFormModalProps) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const clientsQuery = useClients();
  const [formState, setFormState] = useState<PaymentFormState>(getInitialFormState);
  const [formError, setFormError] = useState<string | null>(null);

  const isEditMode = Boolean(editingPayment);

  useEffect(() => {
    if (isOpen && editingPayment) {
      setFormState({
        clientId: editingPayment.clientId,
        amount: editingPayment.amount.toString(),
        paymentMethod: editingPayment.paymentMethod || 'UPI',
        status: editingPayment.status,
        dueDate: toDateInputValue(editingPayment.dueAt),
        paidDate: toDateInputValue(editingPayment.paidAt),
        notes: editingPayment.notes || '',
      });
    } else if (isOpen && !editingPayment) {
      const initialState = getInitialFormState();
      // Pre-fill clientId if provided
      if (clientId) {
        initialState.clientId = clientId;
      }
      setFormState(initialState);
    }
  }, [isOpen, editingPayment, clientId]);

  const paymentMutation = useMutation({
    mutationFn: async (payload: PaymentFormState) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }

      let dueAt = null;
      if (payload.status !== 'paid') {
        // Due date is required for pending/expired payments
        dueAt = combineDateTime(
          payload.dueDate,
          editingPayment?.dueAt
        );
        if (!dueAt) {
          throw new Error('Due date is required for pending/expired payments.');
        }
      } else {
        // Due date is optional for paid payments
        dueAt = combineDateTime(
          payload.dueDate,
          editingPayment?.dueAt
        );
      }

      let paidAt = null;
      if (payload.status === 'paid') {
        paidAt = combineDateTime(payload.paidDate);
        if (!paidAt) {
          throw new Error('Paid date is required for paid payments.');
        }
      }

      const body = {
        amount: parseFloat(payload.amount),
        currency: 'INR',
        payment_method: payload.paymentMethod.trim() || null,
        status: payload.status,
        due_at: dueAt,
        paid_at: paidAt,
        notes: payload.notes.trim() || null,
      };

      if (editingPayment) {
        return apiRequest(
          `/api/clients/${payload.clientId}/payments/${editingPayment.id}`,
          {
            method: 'PATCH',
            body: JSON.stringify(body),
          },
          session.access_token
        );
      } else {
        return apiRequest(
          `/api/clients/${payload.clientId}/payments`,
          {
            method: 'POST',
            body: JSON.stringify(body),
          },
          session.access_token
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['payments', formState.clientId] });
      onSuccess(
        editingPayment ? 'Payment updated successfully' : 'Payment added successfully'
      );
      handleClose();
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to save payment.';
      setFormError(message);
      onError(message);
    },
  });

  const handleSavePayment = () => {
    setFormError(null);

    if (!formState.clientId) {
      setFormError('Please select a client.');
      return;
    }

    if (!formState.amount || parseFloat(formState.amount) <= 0) {
      setFormError('Please enter a valid amount.');
      return;
    }

    // Due date is required for pending/expired, optional for paid
    if (formState.status !== 'paid' && !formState.dueDate) {
      setFormError('Please enter due date for pending/expired payments.');
      return;
    }

    // Paid date is required when status is paid
    if (formState.status === 'paid' && !formState.paidDate) {
      setFormError('Please enter paid date for paid payments.');
      return;
    }

    paymentMutation.mutate(formState);
  };

  const handleClose = () => {
    setFormState(getInitialFormState());
    setFormError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-[#212121]">
            {isEditMode ? 'Edit Payment' : 'Add Payment'}
          </h2>
          <button onClick={handleClose} className="text-gray-500">
            <XIcon className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {formError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              {formError}
            </div>
          )}
          {/* Only show client dropdown if no clientId is pre-selected */}
          {!clientId ? (
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Client {!isEditMode && <span className="text-red-500">*</span>}
              </label>
              <select
                value={formState.clientId}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    clientId: e.target.value,
                  }))
                }
                disabled={isEditMode}
                className={`w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all ${
                  isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Select a client...</option>
                {(clientsQuery.data ?? []).map((client: ClientRecord) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              {isEditMode && editingPayment && (
                <p className="text-xs text-gray-500 mt-1">
                  Client cannot be changed when editing
                </p>
              )}
            </div>
          ) : null}
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Amount (₹) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formState.amount}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  amount: e.target.value,
                }))
              }
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
              placeholder="0"
              min="0"
              step="1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Payment Method
            </label>
            <select
              value={formState.paymentMethod}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  paymentMethod: e.target.value,
                }))
              }
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            >
              <option value="">Not specified</option>
              {paymentMethods.map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Status
              </label>
              <select
                value={formState.status}
                onChange={(e) => {
                  const newStatus = e.target.value as PaymentFormState['status'];
                  setFormState((prev) => {
                    // Auto-fill paid date if changing to 'paid' and it's empty
                    if (newStatus === 'paid' && !prev.paidDate) {
                      return {
                        ...prev,
                        status: newStatus,
                        paidDate: getCurrentLocalDate(),
                      };
                    }
                    return {
                      ...prev,
                      status: newStatus,
                    };
                  });
                }}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Due Date{' '}
              {formState.status !== 'paid' && <span className="text-red-500">*</span>}
            </label>
            <input
              type="date"
              value={formState.dueDate}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  dueDate: e.target.value,
                }))
              }
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            />
          </div>
          {formState.status === 'paid' && (
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Paid On <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formState.paidDate}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    paidDate: e.target.value,
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Notes
            </label>
            <textarea
              value={formState.notes}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all resize-none"
              placeholder="e.g., Advance for 3-month plan"
            />
          </div>
        </div>
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-3 sticky bottom-0">
          <button
            onClick={handleClose}
            className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSavePayment}
            disabled={paymentMutation.isPending}
            className={`flex-1 font-semibold py-3 rounded-xl transition-colors ${
              paymentMutation.isPending
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-[#5B8E87] text-white hover:bg-[#4F7A74]'
            }`}
          >
            {paymentMutation.isPending ? 'Saving...' : 'Save Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}
